import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  ButtonBase,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { LIGHT_RAIL, railTheme } from "../../theme/rail";
import { useTheme as useThemeMode } from "../../context/ThemeContext";
import { ChevronRight, Search } from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { NavigationItem } from "../../types";
import { NavEntry, useNavigation } from "./useNavigation";
import { JUMP_SHORTCUT } from "./NavJumpDialog";

interface SidebarProps {
  onItemClick?: () => void;
  /** Opens the page search. */
  onSearch?: () => void;
  collapsed?: boolean;
}

const ROW_HEIGHT = 36;
const SUB_ROW_HEIGHT = 32;
// Geometry of a submenu: its rail runs under the centre of the group's icon,
// and the children's text starts a little to its right.
const ROW_MARGIN = 8;
const ROW_PADDING = 10;
const ICON_SIZE = 20;
const RAIL_X = ROW_MARGIN + ROW_PADDING + ICON_SIZE / 2;
const SUB_INDENT = RAIL_X + 12;

// Generate dynamic abbreviation from center name
const getCenterAbbreviation = (centerName: string): string => {
  if (!centerName) return "E";
  const words = centerName.split(" ").filter((word) => word.length > 0);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join("");
};

/** Children of a group, with their section headings when they span more than one. */
const withSections = (submenu: NavigationItem[]) => {
  const showSections = new Set(submenu.map((s) => s.section).filter(Boolean)).size > 1;
  return submenu.map((item, index) => ({
    item,
    heading: showSections && item.section && item.section !== submenu[index - 1]?.section ? item.section : null,
  }));
};

/** The sidebar wears its own theme: a soft light rail beside light pages, the navy rail in dark mode. */
const Sidebar: React.FC<SidebarProps> = (props) => {
  const { mode } = useThemeMode();
  return (
    <ThemeProvider theme={railTheme(mode)}>
      <SidebarContent {...props} />
    </ThemeProvider>
  );
};

const SidebarContent: React.FC<SidebarProps> = ({ onItemClick, onSearch, collapsed = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const { areas, activePath, activeGroup } = useNavigation();
  const primary = theme.palette.primary.main;

  // One group open at a time, and always the one holding the current page.
  const [openGroup, setOpenGroup] = useState<string | null>(activeGroup);
  useEffect(() => {
    if (activeGroup) setOpenGroup(activeGroup);
  }, [activeGroup]);

  // Bring the current page into view on a long menu.
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [activePath, collapsed]);

  // Build a full logo URL from the stored filename
  const schoolLogoUrl = useMemo(() => {
    const logo = user?.schoolId?.logo;
    if (!logo) return null;
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    const origin = apiUrl.replace(/\/api\/?$/, "");
    return `${origin}/uploads/logos/${logo}`;
  }, [user?.schoolId?.logo]);

  const go = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const light = theme.palette.mode === "light";
  // Where you are: a raised white chip on the light rail, a tint of the accent on the dark one.
  const currentSx = light
    ? { bgcolor: LIGHT_RAIL.raised, boxShadow: LIGHT_RAIL.raisedShadow }
    : { bgcolor: alpha(primary, 0.16), boxShadow: "none" };
  const currentHoverSx = light ? { bgcolor: LIGHT_RAIL.raised } : { bgcolor: alpha(primary, 0.22) };
  const restSx = { bgcolor: "transparent", boxShadow: "none" };
  const hoverTint = alpha(theme.palette.text.primary, light ? 0.045 : 0.06);
  // The accent sits on the icon and a slim bar; on the dark rail the bar glows.
  const accentBar = { bgcolor: "primary.main", boxShadow: light ? "none" : `0 0 10px ${alpha(primary, 0.55)}` };
  const ease = (props: string[]) =>
    theme.transitions.create(props, { duration: 160, easing: theme.transitions.easing.easeOut });
  const focusRing = { "&.Mui-focusVisible": { outline: `2px solid ${alpha(primary, 0.5)}`, outlineOffset: -2 } };

  // A top-level row. `current` paints it as where you are: a tint and a
  // short accent bar on the sidebar's edge.
  const rowSx = (current: boolean, strong: boolean) => ({
    position: "relative" as const,
    minHeight: ROW_HEIGHT,
    mx: `${ROW_MARGIN}px`,
    mb: "2px",
    px: collapsed ? 0 : `${ROW_PADDING}px`,
    py: 0,
    justifyContent: collapsed ? "center" : "flex-start",
    borderRadius: "8px",
    ...(current ? currentSx : restSx),
    color: current || strong ? "text.primary" : "text.secondary",
    transition: ease(["background-color", "color", "box-shadow"]),
    "&::before": {
      content: '""',
      position: "absolute",
      left: -ROW_MARGIN,
      top: "50%",
      width: 3,
      height: current ? 18 : 0,
      transform: "translateY(-50%)",
      borderRadius: "0 3px 3px 0",
      ...accentBar,
      transition: ease(["height"]),
    },
    "&:hover": {
      ...(current ? currentHoverSx : { bgcolor: hoverTint }),
      color: "text.primary",
    },
    "& .MuiListItemIcon-root": {
      minWidth: collapsed ? 0 : ICON_SIZE + 12,
      color: current ? "primary.main" : "inherit",
      opacity: current || strong ? 1 : 0.8,
      "& svg": { fontSize: ICON_SIZE },
    },
    ...focusRing,
  });

  const label = (text: string, weight: number) => (
    <ListItemText
      sx={{ my: 0 }}
      primary={text}
      primaryTypographyProps={{ fontSize: "0.8125rem", fontWeight: weight, letterSpacing: "0.003em", noWrap: true }}
    />
  );

  // Collapsed rail: a group's children in a flyout beside its icon.
  const flyout = (group: NavEntry) => (
    <Box sx={{ py: 0.5, minWidth: 196 }}>
      <Typography sx={{ px: 1.5, pt: 0.5, pb: 0.75, fontSize: "0.75rem", fontWeight: 700, color: "text.primary" }}>
        {group.title}
      </Typography>
      {withSections(group.submenu!).map(({ item, heading }) => (
        <React.Fragment key={item.path}>
          {heading && <SectionHeading text={heading} sx={{ px: 1.5, pt: 1 }} />}
          <ListItemButton
            onClick={() => go(item.path!)}
            sx={{
              minHeight: SUB_ROW_HEIGHT,
              mx: 0.5,
              px: 1,
              py: 0,
              borderRadius: "6px",
              color: item.path === activePath ? "text.primary" : "text.secondary",
              ...(item.path === activePath ? currentSx : restSx),
              "&:hover": { ...(item.path === activePath ? currentHoverSx : { bgcolor: hoverTint }), color: "text.primary" },
            }}
          >
            {label(item.title, item.path === activePath ? 600 : 500)}
          </ListItemButton>
        </React.Fragment>
      ))}
    </Box>
  );

  const renderLeaf = (item: NavEntry) => {
    const current = item.path === activePath;
    return (
      <Tooltip key={item.path} title={collapsed ? item.title : ""} placement="right" disableInteractive>
        <ListItemButton data-active={current} onClick={() => go(item.path!)} sx={rowSx(current, false)}>
          <ListItemIcon>{item.icon}</ListItemIcon>
          {!collapsed && label(item.title, current ? 600 : 500)}
        </ListItemButton>
      </Tooltip>
    );
  };

  const renderGroup = (group: NavEntry) => {
    const holdsCurrent = group.title === activeGroup;
    const open = !collapsed && openGroup === group.title;
    // While its children are hidden, the group itself shows where you are.
    const current = holdsCurrent && !open;

    if (collapsed) {
      return (
        <Tooltip
          key={group.title}
          title={flyout(group)}
          placement="right-start"
          leaveDelay={120}
          componentsProps={{
            tooltip: {
              sx: {
                p: 0,
                maxWidth: 280,
                bgcolor: "background.paper",
                color: "text.primary",
                border: 1,
                borderColor: "divider",
                borderRadius: "10px",
                boxShadow: theme.shadows[8],
              },
            },
          }}
        >
          <ListItemButton data-active={holdsCurrent} onClick={() => go(group.submenu![0].path!)} sx={rowSx(holdsCurrent, false)}>
            <ListItemIcon>{group.icon}</ListItemIcon>
          </ListItemButton>
        </Tooltip>
      );
    }

    return (
      <Box key={group.title}>
        <ListItemButton
          data-active={current}
          aria-expanded={open}
          onClick={() => setOpenGroup(open ? null : group.title)}
          sx={rowSx(current, holdsCurrent)}
        >
          <ListItemIcon>{group.icon}</ListItemIcon>
          {label(group.title, holdsCurrent ? 600 : 500)}
          <ChevronRight
            sx={{
              fontSize: 16,
              color: "text.disabled",
              transform: open ? "rotate(90deg)" : "none",
              transition: ease(["transform"]),
            }}
          />
        </ListItemButton>
        <Collapse in={open} timeout={200} unmountOnExit>
          <Box
            sx={{
              position: "relative",
              pt: "2px",
              pb: "6px",
              "&::before": {
                content: '""',
                position: "absolute",
                left: RAIL_X,
                top: 2,
                bottom: 8,
                width: "1px",
                bgcolor: "divider",
              },
            }}
          >
            {withSections(group.submenu!).map(({ item, heading }) => {
              const active = item.path === activePath;
              return (
                <React.Fragment key={item.path}>
                  {heading && <SectionHeading text={heading} sx={{ pl: `${SUB_INDENT + ROW_PADDING}px`, pt: 1.25 }} />}
                  <ListItemButton
                    data-active={active}
                    onClick={() => go(item.path!)}
                    sx={{
                      position: "relative",
                      minHeight: SUB_ROW_HEIGHT,
                      ml: `${SUB_INDENT}px`,
                      mr: `${ROW_MARGIN}px`,
                      mb: "1px",
                      px: `${ROW_PADDING}px`,
                      py: 0,
                      borderRadius: "6px",
                      ...(active ? currentSx : restSx),
                      color: active ? "text.primary" : "text.secondary",
                      transition: ease(["background-color", "color", "box-shadow"]),
                      // The rail lights up beside the current page.
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: RAIL_X - SUB_INDENT - 0.5,
                        top: 7,
                        bottom: 7,
                        width: 2,
                        borderRadius: 1,
                        ...(active ? accentBar : { bgcolor: "transparent" }),
                        transition: ease(["background-color"]),
                      },
                      "&:hover": {
                        ...(active ? currentHoverSx : { bgcolor: hoverTint }),
                        color: "text.primary",
                        "&::before": active ? {} : { bgcolor: alpha(theme.palette.text.primary, 0.3) },
                      },
                      ...focusRing,
                    }}
                  >
                    {label(item.title, active ? 600 : 500)}
                  </ListItemButton>
                </React.Fragment>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        color: "text.primary",
        // Dark: a faint lift at the top gives the ink some depth. Light: a plain rail edged by a hairline.
        background: light
          ? theme.palette.background.paper
          : `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.045)} 0%, ${alpha(theme.palette.common.white, 0)} 220px), ${theme.palette.background.paper}`,
        borderRight: light ? 1 : 0,
        borderColor: "divider",
      }}
    >
      {/* Brand Header */}
      <Box
        sx={{
          px: collapsed ? 1 : 2,
          py: 1.5,
          minHeight: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 1.5,
        }}
      >
        <Tooltip title={collapsed ? (user?.schoolId?.name || "Edubook") : ""} placement="right">
          <Box
            sx={{
              width: collapsed ? 44 : 52,
              height: collapsed ? 40 : 44,
              borderRadius: "10px",
              flexShrink: 0,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...(schoolLogoUrl
                ? {
                    bgcolor: "common.white",
                    boxShadow: light
                      ? LIGHT_RAIL.raisedShadow
                      : `0 0 0 1px ${alpha(theme.palette.common.white, 0.12)}, 0 4px 12px ${alpha(theme.palette.common.black, 0.35)}`,
                  }
                : {
                    background: `linear-gradient(135deg, ${primary} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    letterSpacing: "0.5px",
                    boxShadow: `0 4px 12px ${alpha(light ? primary : theme.palette.common.black, light ? 0.25 : 0.35)}`,
                  }),
            }}
          >
            {schoolLogoUrl ? (
              <Box
                component="img"
                src={schoolLogoUrl}
                alt={user?.schoolId?.name || "School Logo"}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
                sx={{ width: "100%", height: "100%", objectFit: "contain", p: 0.35 }}
              />
            ) : (
              getCenterAbbreviation(user?.schoolId?.name || "Edubook")
            )}
          </Box>
        </Tooltip>
        {!collapsed && (
          <Box sx={{ overflow: "hidden", minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary", lineHeight: 1.25, letterSpacing: "-0.01em" }}>
              Edubook
            </Typography>
            <Typography noWrap sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.3 }}>
              {user?.schoolId?.name || "School Management"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Page search */}
      {onSearch && (
        <Box sx={{ px: `${ROW_MARGIN}px`, pb: 1 }}>
          <Tooltip title={collapsed ? `Search pages  ${JUMP_SHORTCUT}` : ""} placement="right" disableInteractive>
            <ButtonBase
              onClick={onSearch}
              aria-label="Search pages"
              sx={{
                width: "100%",
                height: ROW_HEIGHT,
                px: collapsed ? 0 : 1.25,
                gap: 1,
                justifyContent: collapsed ? "center" : "flex-start",
                borderRadius: "8px",
                border: 1,
                borderColor: "divider",
                bgcolor: light ? LIGHT_RAIL.raised : alpha(theme.palette.text.primary, 0.04),
                color: "text.secondary",
                transition: ease(["background-color", "border-color", "color"]),
                "&:hover": { bgcolor: hoverTint, borderColor: alpha(theme.palette.text.primary, 0.2), color: "text.primary" },
                ...focusRing,
              }}
            >
              <Search sx={{ fontSize: 18 }} />
              {!collapsed && (
                <>
                  <Typography sx={{ fontSize: "0.8125rem", flex: 1, textAlign: "left" }}>Search pages</Typography>
                  <Box
                    component="kbd"
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: "text.disabled",
                      px: 0.75,
                      py: 0.125,
                      borderRadius: "5px",
                      border: 1,
                      borderColor: "divider",
                      bgcolor: alpha(theme.palette.text.primary, 0.05),
                    }}
                  >
                    {JUMP_SHORTCUT}
                  </Box>
                </>
              )}
            </ButtonBase>
          </Tooltip>
        </Box>
      )}

      {/* Navigation */}
      <List
        ref={listRef}
        component="nav"
        disablePadding
        sx={{
          flexGrow: 1,
          pb: 1.5,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.16)} transparent`,
          "&::-webkit-scrollbar-thumb": {
            background: alpha(theme.palette.text.primary, 0.16),
            borderRadius: 4,
            "&:hover": { background: alpha(theme.palette.text.primary, 0.28) },
          },
        }}
      >
        {areas.map((area, index) => (
          <Box key={area.label || "home"} component="section" sx={{ pt: index === 0 ? 0.5 : 0 }}>
            {area.label &&
              (collapsed ? (
                <Box sx={{ mx: "auto", my: 1.25, width: 20, height: "1px", bgcolor: "divider" }} />
              ) : (
                <SectionHeading text={area.label} sx={{ px: `${ROW_MARGIN + ROW_PADDING}px`, pt: 2.25, pb: 0.75 }} />
              ))}
            {area.items.map((item) => (item.submenu ? renderGroup(item) : renderLeaf(item)))}
          </Box>
        ))}
      </List>

      {!collapsed && (
        <Typography sx={{ py: 1, fontSize: "0.65rem", color: "text.disabled", letterSpacing: "0.02em", textAlign: "center" }}>
          v1.0.0
        </Typography>
      )}
    </Box>
  );
};

const SectionHeading: React.FC<{ text: string; sx?: object }> = ({ text, sx }) => (
  <Typography
    sx={{
      pb: 0.5,
      fontSize: "0.66rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "text.disabled",
      userSelect: "none",
      ...sx,
    }}
  >
    {text}
  </Typography>
);

export default Sidebar;
